import { BRANDS } from "app/constants";
import AmountContainer from "app/pages/.shared/AmountContainer";
import { BackButton } from "app/components/ui/buttons/BackButton";
import LogoBrandContainer from "app/pages/.shared/LogoBrand/LogoBrandContainer";
import RelativeLink from "app/pages/.shared/RelativeLink";
import AccountMenuContainer from "app/pages/Header/AccountMenu/AccountMenuContainer";
import { sendTagOnContactCall } from "app/utils/analytics";
import { brandPropTypes } from "app/utils/propTypes";
import { checkIfComingFromRemarketingEmail } from "app/utils/urlDataExtractor";
import { displayable } from "app/utils/utils";
import classNames from "classnames";
import { isEmpty } from "lodash";
import PropTypes from "prop-types";
import { memo } from "react";
import { FormattedMessage } from "react-intl";
import { useLocation, useParams, useSearchParams } from "react-router-dom";
import "./Header.scss";
import ShopSwitcherContainer from "app/pages/Home/ShopSwitcher/ShopSwitcherContainer";
import ShopSwitcher from "app/pages/Home/ShopSwitcher/ShopSwitcher";
import Typography, { TYPOGRAPHY_VARIANTS } from "app/pages/.shared/Typography/Typography";
import MiniSearchContainer from "app/features/SearchEngine/MiniSearch/MiniSearchContainer";
import { MoneyBagCircleFilledIcon } from "app/icons/finance";
import { SupportStrokeIcon } from "app/icons/profile";

const Header = ({
	brandCode,
	isMobile = false,
	userIsConnected = false,
	enableCredits = false,
	fallbackRedirectUrl,
	merchandisingCode,
	dialPhone,
	creditsAmount = 0,
	onClickBackBtn = () => {},
	showMenuItems = true,
	showDialPhone = false,
	showBackBtn = false,
	inscriptionCreditCampaign = {},
	isTransparentHeader = false,
	showMiniSearch = false,
}) => {
	const { pathname } = useLocation();
	const { productUri } = useParams();
	const [searchParams] = useSearchParams();

	const isFicheProduitPage = Boolean(productUri);
	const isSdpSearchPage = pathname.includes("sdp/search");
	const isRootPage = pathname === "/";
	const shouldDisplayShopSwitcher =
		!pathname.includes("/booking/search") &&
		!pathname.includes("/booking/quote") &&
		!pathname.includes("/booking/payment") &&
		!pathname.includes("/booking/auth") &&
		!pathname.includes("/booking/confirmation");
	const isSdpRootPage = pathname === "/sdp" || isRootPage;
	const applyTransparentHeader = isTransparentHeader && (isSdpSearchPage || isSdpRootPage);
	const headerClass = classNames("header", {
		"header--transparent": applyTransparentHeader,
	});

	const isCurrentPathFallbackUrl = pathname === fallbackRedirectUrl;

	const isBrandEH = brandCode === BRANDS.EK;
	const logoClass = classNames("header__logo", {
		"header__logo--eh": isBrandEH,
		"header__logo--disabled": isCurrentPathFallbackUrl,
		"header__logo--eh-small": isBrandEH && !isSdpSearchPage && !isSdpRootPage,
	});

	const isLogoWhite =
		brandCode === BRANDS.CD || (brandCode === BRANDS.TO && applyTransparentHeader);

	const isBrandXX = brandCode === BRANDS.XX;

	const isComingFromRemarketingEmail = checkIfComingFromRemarketingEmail(
		searchParams.get("utm_source"),
		searchParams.get("utm_medium")
	);

	const shouldRedirectToProductPage =
		isComingFromRemarketingEmail || searchParams.get("redirectToProductPage") === "true";

	// eslint-disable-next-line no-nested-ternary
	const fallbackUrlInProductPage = merchandisingCode
		? {
				pathname: "/merch",
				search: `?code=${merchandisingCode}`,
				merchCode: merchandisingCode,
		  }
		: shouldRedirectToProductPage
		? {
				pathname: `/${searchParams.get("productUri")}`,
		  }
		: {
				pathname: isBrandXX ? "/listing" : fallbackRedirectUrl,
		  };

	const inscriptionCreditCampaignAvailable =
		!isEmpty(inscriptionCreditCampaign) && displayable(inscriptionCreditCampaign);
	const shouldShowCreditItem =
		userIsConnected && (inscriptionCreditCampaignAvailable || creditsAmount > 0);

	return (
		<header className={headerClass}>
			<div className="header__left">
				{showBackBtn && (
					<div className="header__back">
						<BackButton
							{...(isFicheProduitPage || shouldRedirectToProductPage) && {
								to: fallbackUrlInProductPage,
							}}
							onClick={onClickBackBtn}
						/>
					</div>
				)}

				<div className={logoClass}>
					<RelativeLink
						to={{ pathname: fallbackRedirectUrl }}
						data-testid="header-back-link"
					>
						<LogoBrandContainer isWhite={isLogoWhite} />
					</RelativeLink>
				</div>
			</div>

			{!isMobile && showMiniSearch && <MiniSearchContainer />}

			<div className="header__right">
				{showMenuItems && enableCredits && userIsConnected && shouldShowCreditItem && (
					<RelativeLink
						to={{ pathname: "/account/mycredits" }}
						className="header__credit"
						data-testid="header-credit-link"
					>
						<MoneyBagCircleFilledIcon />
						<Typography variant={TYPOGRAPHY_VARIANTS.SMALL} isBold>
							{creditsAmount > 0 ? (
								<AmountContainer amount={creditsAmount} />
							) : (
								<div className="header__credit-label">
									<FormattedMessage id="menu.item.my.credits" />
								</div>
							)}
						</Typography>
					</RelativeLink>
				)}

				{shouldDisplayShopSwitcher && !isMobile && (
					<ShopSwitcherContainer renderComponent={props => <ShopSwitcher {...props} />} />
				)}

				{showMenuItems && <AccountMenuContainer />}

				{isMobile && showDialPhone && dialPhone && (
					<div className="header__phone">
						<a
							className="header__phone-button"
							onClick={sendTagOnContactCall}
							href={`tel:${dialPhone}`}
							data-testid="header-phone"
						>
							<SupportStrokeIcon height={30} width={30} />
							<Typography variant={TYPOGRAPHY_VARIANTS.XSMALL} isBold>
								<FormattedMessage id="header.help" />
							</Typography>
						</a>
					</div>
				)}
			</div>
		</header>
	);
};

Header.propTypes = {
	brandCode: brandPropTypes,
	isMobile: PropTypes.bool,
	userIsConnected: PropTypes.bool,
	enableCredits: PropTypes.bool,
	onClickBackBtn: PropTypes.func,
	previousUrl: PropTypes.string,
	fallbackRedirectUrl: PropTypes.string,
	dialPhone: PropTypes.string,
	merchandisingCode: PropTypes.string,
	creditsAmount: PropTypes.number,
	showMenuItems: PropTypes.bool,
	showDialPhone: PropTypes.bool,
	showBackBtn: PropTypes.bool,
	inscriptionCreditCampaign: PropTypes.object,
	isTransparentHeader: PropTypes.bool,
	showMiniSearch: PropTypes.bool,
};

export default memo(Header);
